import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { Client, Invoice } from '../models/table-models';
import { getInvoiceClientId, useGetClient as getClient, saveClient } from '../utils/clients-hooks';
import { Role } from '../utils/Enums/Roles';
import { ClientRegistryCheck } from './NewRequest/ClientRegistryCheck';
import { useTranslation } from 'react-i18next';

/** Hold numbering until the operator has chosen which client details to use. */
export const useInvoiceRegistryCheck = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'registryCheck' });
    const { hasPermission } = useContext(AuthContext);
    const canUpdate = hasPermission([Role.COORDINATOR]) || !hasPermission([Role.DISTRIBUTOR, Role.INSTALLER]);

    const locked = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const [busy, setBusy] = useState(false);
    const [registryVisible, setRegistryVisible] = useState(false);
    const [error, setError] = useState('');
    const [pending, setPending] = useState<{ client: Client; proceed: () => Promise<void> } | null>(null);
    const release = () => {
        locked.current = false;
        if (mounted.current) {
            setBusy(false);
            setPending(null);
            setRegistryVisible(false);
        }
    };

    const checkBeforeIssue = async (invoice: Invoice, proceed: () => Promise<void>) => {
        if (locked.current) return;
        locked.current = true;
        setBusy(true);
        setError('');
        try {
            if (
                invoice.documentType?.toLowerCase() === 'invoice' &&
                !invoice.document?.invoiceNumber &&
                !invoice.invoiceNumber
            ) {
                const id = await getInvoiceClientId(invoice.id!, invoice.isSaleInvoice);
                if (!mounted.current) return;
                if (!id) throw new Error('Missing client');
                const client = await getClient(String(id));
                if (!mounted.current) return;
                if (!client) throw new Error('Missing client');
                if (client.company) {
                    setPending({ client, proceed });
                    return;
                }
            }
            await proceed();
            release();
        } catch {
            if (mounted.current) setError('preparationFailed');
            release();
        }
    };

    const proceed = async () => {
        if (!mounted.current) return;
        await pending!.proceed();
        release();
    };

    const registryDialog = (
        <>
            {busy && !registryVisible && (
                <div role='status' className='position-absolute top-0 end-0 m-2 px-3 py-2 bg-light rounded shadow-sm' style={{ zIndex: 1 }}>
                    <Spinner animation='border' size='sm' /> {t('processing')}
                </div>
            )}
            {error && <Alert variant='danger'>{t(error)}</Alert>}
            {pending && (
                <ClientRegistryCheck
                    client={pending.client}
                    canUpdate={canUpdate}
                    onVisibilityChange={setRegistryVisible}
                    onCancel={release}
                    onContinue={proceed}
                    onConfirm={async (client) => {
                        const saved = await saveClient(client);
                        if (!saved) throw new Error('Client update failed');
                        localStorage.removeItem('selectedClient');
                        await proceed();
                    }}
                />
            )}
        </>
    );

    return { checkBeforeIssue, registryDialog, registryBusy: busy };
};
